/**
 * AmbassadorAutoSignIn — Cross-domain sign-in landing for fresh ambassadors.
 *
 * The public onboarding page on peaceofsleep.vip creates the account and
 * receives a Supabase session back. That session lives on the wrong
 * domain — peaceofsleep.vip can't write auth storage on cleanmybed.com.
 *
 * To hand the session across, the onboarding page redirects the user to
 *   https://{cc}.app.cleanmybed.com/ambassador/auto-signin#access_token=...&refresh_token=...
 *
 * This page reads the tokens from the URL fragment (the part after the
 * hash — never sent to the server, only readable by client JavaScript),
 * calls supabase.auth.setSession(), then redirects to /ambassador.
 *
 * If the tokens are missing or rejected, the user is bounced to the
 * regular login page where they can sign in by hand.
 *
 * URL: /ambassador/auto-signin#access_token=...&refresh_token=...
 */

import { useEffect, useState } from 'react';
import { POS_GOLD } from '../../config/posRoomStatus';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import '../pos/pos-theme.css';
import '../pos/pages/pos-login.css';

/** How long to wait before showing any visible UI. setSession typically
 *  resolves in 100-200ms, so under this threshold the user sees no
 *  intermediate state at all — the page transition is invisible. */
const REVEAL_DELAY_MS = 350;

function AmbassadorAutoSignIn() {
  const { supabase } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'working' | 'failed'>('working');
  const [errorMessage, setErrorMessage] = useState('');
  const [showSpinner, setShowSpinner] = useState(false);

  useEffect(() => {
    // Only reveal a spinner if the exchange takes longer than the threshold.
    // Avoids a "card flashes and disappears" effect on a fast connection.
    const t = window.setTimeout(() => setShowSpinner(true), REVEAL_DELAY_MS);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!supabase) return;

    async function exchange() {
      const hash = window.location.hash.startsWith('#')
        ? window.location.hash.substring(1)
        : window.location.hash;
      const params = new URLSearchParams(hash);
      const accessToken = params.get('access_token');
      const refreshToken = params.get('refresh_token');

      if (!accessToken || !refreshToken) {
        setStatus('failed');
        setShowSpinner(true);
        setErrorMessage('We could not pick up your session from the previous step.');
        return;
      }

      try {
        const { error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
        if (error) throw error;

        // Strip the tokens out of the URL bar before navigating away —
        // anyone shoulder-surfing or screenshotting the address bar
        // shouldn't see them lingering.
        try {
          window.history.replaceState(null, '', '/ambassador/auto-signin');
        } catch {
          // ignore
        }

        navigate('/ambassador', { replace: true });
      } catch (err) {
        console.error('[ambassador-auto-signin] setSession failed:', err);
        setStatus('failed');
        setShowSpinner(true);
        setErrorMessage('Your sign-in link could not be used. Please sign in with your email and password.');
      }
    }

    exchange();
  }, [supabase, navigate]);

  // Background matches the Ambassador portal layout (linen) so the
  // transition into the dashboard has no perceptible colour change.
  // No card / no headline on the happy path — just an invisible hold
  // while setSession resolves. A subtle spinner appears only if the
  // exchange is taking longer than ~350ms.
  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--pos-linen, #EAE7E1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
      }}
    >
      {status === 'working' && showSpinner && (
        <div className="text-center">
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              border: '2px solid rgba(0,0,0,0.1)',
              borderTopColor: POS_GOLD,
              animation: 'pos-spin 0.8s linear infinite',
              margin: '0 auto',
            }}
          />
        </div>
      )}

      {status === 'failed' && (
        <div
          style={{
            background: 'var(--color-white)',
            border: '1px solid rgba(0,0,0,0.08)',
            borderRadius: 12,
            padding: 32,
            maxWidth: 420,
            width: '100%',
            textAlign: 'center',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          }}
        >
          <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 600, color: 'var(--color-sidebar)' }}>One moment more</h2>
          <p style={{ margin: '0 0 20px', fontSize: 14, color: 'var(--color-sidebar-hover)', lineHeight: 1.6 }}>{errorMessage}</p>
          <button
            type="button"
            onClick={() => navigate('/ambassador/login', { replace: true })}
            style={{
              padding: '12px 32px',
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: '1px',
              textTransform: 'uppercase',
              background: POS_GOLD,
              color: 'var(--color-sidebar)',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            Sign In
          </button>
        </div>
      )}
    </div>
  );
}

export default AmbassadorAutoSignIn;
